import React from 'react';
import { Link } from 'react-router-dom';
import { Box, Typography, Button, Avatar, Chip, Grid } from '@mui/material';
import DashboardIcon         from '@mui/icons-material/Dashboard';
import RestaurantIcon        from '@mui/icons-material/Restaurant';
import DirectionsCarIcon     from '@mui/icons-material/DirectionsCar';
import LocalShippingIcon     from '@mui/icons-material/LocalShipping';
import ShoppingCartIcon      from '@mui/icons-material/ShoppingCart';
import GroupIcon             from '@mui/icons-material/Group';
import PersonIcon            from '@mui/icons-material/Person';
import ArrowForwardIcon      from '@mui/icons-material/ArrowForward';
import DirectionsCarFilledIcon from '@mui/icons-material/DirectionsCarFilled';
import SettingsIcon          from '@mui/icons-material/Settings';
import WavingHandIcon        from '@mui/icons-material/WavingHand';
import PedalBikeIcon         from '@mui/icons-material/PedalBike';
import RestaurantMenuIcon    from '@mui/icons-material/RestaurantMenu';
import RocketLaunchIcon      from '@mui/icons-material/RocketLaunch';
import { useAuth }           from '../context/AuthContext';
import { TZ }                from '../utils/date';

// Charte Usafiri
const C = {
  purple:      '#552583',
  purpleLight: '#7a3aad',
  purpleDark:  '#33154f',
  gold:        '#FDB927',
  bg:          '#E4F0F6',
  paper:       'rgba(255,255,255,0.75)',
  text:        '#1a1a1a',
  textSoft:    '#5a5a5a',
  textMuted:   '#8a8a8a',
};

const SERVICES = [
  {
    title: 'Restauration',
    desc: 'Commandez vos plats préférés',
    icon: RestaurantIcon,
    path: '/restaurants',
    historyPath: '/restaurant/orders',
    image: 'https://images.unsplash.com/photo-1555939594-58d7cb561ad1?w=600&h=400&fit=crop',
    color: '#f97316',
    bg: 'rgba(249,115,22,0.09)',
    border: 'rgba(249,115,22,0.22)',
  },
  {
    title: 'Covoiturage',
    desc: 'Partagez vos trajets',
    icon: GroupIcon,
    path: '/carpool',
    historyPath: '/carpool/bookings',
    image: 'https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?w=600&h=400&fit=crop',
    color: C.purple,
    bg: `rgba(85,37,131,0.09)`,
    border: `rgba(85,37,131,0.22)`,
    comingSoon: true,
  },
  {
    title: 'Location auto',
    desc: 'Louez un véhicule',
    icon: DirectionsCarFilledIcon,
    path: '/car-rental',
    historyPath: '/car-rental/bookings',
    image: 'https://images.unsplash.com/photo-1549317661-bd32c8ce0db2?w=600&h=400&fit=crop',
    color: C.gold,
    bg: `rgba(253,185,39,0.09)`,
    border: `rgba(253,185,39,0.22)`,
    comingSoon: true,
  },
  {
    title: 'VTC',
    desc: 'Réservez une course partout',
    icon: DirectionsCarIcon,
    path: '/vtc',
    historyPath: '/vtc/rides',
    image: 'https://images.unsplash.com/photo-1485291571150-772bcfc10da5?w=600&h=400&fit=crop',
    color: '#0ea5e9',
    bg: 'rgba(14,165,233,0.09)',
    border: 'rgba(14,165,233,0.22)',
    comingSoon: true,
  },
  {
    title: 'Logistique',
    desc: 'Envoyez vos colis rapidement',
    icon: LocalShippingIcon,
    path: '/logistics',
    historyPath: '/logistics/orders',
    image: 'https://images.unsplash.com/photo-1601584115197-04ecc0da31d7?w=600&h=400&fit=crop',
    color: '#8b5cf6',
    bg: 'rgba(139,92,246,0.09)',
    border: 'rgba(139,92,246,0.22)',
    comingSoon: true,
  },
  {
    title: 'Courses',
    desc: 'Shopping livré chez vous',
    icon: ShoppingCartIcon,
    path: '/courses',
    historyPath: '/restaurant/orders',
    image: 'https://images.unsplash.com/photo-1542838132-92c53300491e?w=600&h=400&fit=crop',
    color: '#10b981',
    bg: 'rgba(16,185,129,0.09)',
    border: 'rgba(16,185,129,0.22)',
  },
];

const USER_TYPE_LABELS = {
  client:           'Client',
  driver:           'Chauffeur VTC',
  delivery:         'Livreur',
  restaurant_owner: 'Restaurateur',
  admin:            'Administrateur',
};

const Dashboard = () => {
  const { user } = useAuth();
  const hour     = parseInt(new Date().toLocaleTimeString('fr-FR', { timeZone: TZ, hour: '2-digit', hour12: false }));
  const greeting = hour < 12 ? 'Bonjour' : hour < 18 ? 'Bon après-midi' : 'Bonsoir';
  const initials = `${user?.first_name?.[0] || ''}${user?.last_name?.[0] || ''}`.toUpperCase();
  const dayName  = new Date().toLocaleDateString('fr-FR', { timeZone: TZ, weekday: 'long', day: 'numeric', month: 'long' });

  return (
    <Box sx={{ bgcolor: C.bg, minHeight: '100vh', pb: 10 }}>

      {/* ── HERO ── */}
      <Box sx={{
        background: `linear-gradient(135deg, ${C.purpleDark} 0%, ${C.purple} 55%, ${C.purpleLight} 100%)`,
        pt: { xs: 5, md: 7 },
        pb: { xs: 10, md: 14 },
        px: { xs: 3, sm: 5, md: 8 },
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""', position: 'absolute',
          top: '-40%', right: '-5%',
          width: '55%', height: '200%',
          background: `radial-gradient(ellipse, rgba(253,185,39,0.16) 0%, transparent 65%)`,
        },
        '&::after': {
          content: '""', position: 'absolute',
          bottom: 0, left: 0, right: 0, height: 80,
          background: `linear-gradient(to bottom, transparent, ${C.bg})`,
        },
      }}>
        <Box sx={{ maxWidth: 1100, mx: 'auto', position: 'relative', zIndex: 1 }}>

          {/* Top row : badge rôle + lien profil */}
          <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 5 }}>
            <Chip
              label={USER_TYPE_LABELS[user?.user_type] || user?.user_type}
              size="small"
              sx={{
                bgcolor: 'rgba(253,185,39,0.18)', color: C.gold,
                border: `1px solid rgba(253,185,39,0.4)`,
                fontWeight: 800, fontSize: '0.68rem', letterSpacing: '0.08em',
              }}
            />
            <Box component={Link} to="/profile" sx={{
              display: 'flex', alignItems: 'center', gap: 1,
              color: C.purpleDark, textDecoration: 'none',
              fontSize: '0.82rem', fontWeight: 800,
              px: { xs: 1.5, sm: 2.5 }, py: 1, borderRadius: '12px',
              bgcolor: C.gold,
              border: `1px solid rgba(253,185,39,0.4)`,
              boxShadow: '0 4px 14px rgba(253,185,39,0.35)',
              transition: 'all 0.2s',
              '&:hover': { bgcolor: C.goldLight, boxShadow: '0 6px 20px rgba(253,185,39,0.5)', transform: 'translateY(-1px)' },
            }}>
              <SettingsIcon sx={{ fontSize: 16 }} />
              <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Mon profil</Box>
            </Box>
          </Box>

          {/* Greeting */}
          <Box display="flex" alignItems="center" gap={3}>
            <Avatar sx={{
              width: { xs: 60, sm: 72 }, height: { xs: 60, sm: 72 },
              background: `linear-gradient(135deg, rgba(253,185,39,0.3) 0%, rgba(253,185,39,0.1) 100%)`,
              border: `2.5px solid rgba(253,185,39,0.5)`,
              boxShadow: `0 0 28px rgba(253,185,39,0.25)`,
              fontWeight: 900, fontSize: { xs: '1.2rem', sm: '1.5rem' },
              color: C.gold,
            }}>
              {initials || <PersonIcon />}
            </Avatar>
            <Box>
              <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.82rem', mb: 0.4 }}>
                {greeting} <WavingHandIcon sx={{ fontSize: 18, ml: 0.5, color: C.gold }} />
              </Typography>
              <Typography sx={{
                color: 'white', fontWeight: 900,
                fontSize: { xs: '1.7rem', sm: '2.2rem' },
                lineHeight: 1.1, letterSpacing: '-0.02em',
              }}>
                {user?.first_name} {user?.last_name}
              </Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.78rem', mt: 0.4 }}>
                {dayName}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* ── CONTENU ── */}
      <Box sx={{ maxWidth: 1100, mx: 'auto', px: { xs: 2, sm: 4, md: 8 }, mt: -5, position: 'relative', zIndex: 2 }}>

        {/* ── TABLEAU DE BORD RESTAURATEUR ── */}
        {user?.user_type === 'restaurant_owner' && (
          <Box sx={{
            p: 3, mb: 3, borderRadius: '20px',
            background: `linear-gradient(135deg, #1a0a00 0%, #7c2d12 55%, #c2410c 100%)`,
            boxShadow: `0 8px 32px rgba(194,65,12,0.35)`,
            display: 'flex', alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: 'space-between',
            flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 2, sm: 0 },
            position: 'relative', overflow: 'hidden',
            transition: 'all 0.22s',
            '&:hover': { transform: 'translateY(-3px)', boxShadow: `0 16px 48px rgba(194,65,12,0.45)` },
            '&::before': {
              content: '""', position: 'absolute',
              top: '-40%', right: '-10%',
              width: '60%', height: '200%',
              background: `radial-gradient(ellipse, rgba(253,185,39,0.18) 0%, transparent 65%)`,
            },
          }}>
            <Box sx={{ position: 'relative', zIndex: 1 }}>
              <Box display="flex" alignItems="center" gap={1.5} sx={{ mb: 0.6 }}>
                <Box sx={{
                  width: 40, height: 40, borderRadius: '12px',
                  bgcolor: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255,255,255,0.25)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                  <RestaurantIcon sx={{ color: C.gold, fontSize: 20 }} />
                </Box>
                <Box>
                  <Typography sx={{ color: 'white', fontWeight: 900, fontSize: '1.05rem', lineHeight: 1.2 }}>
                    Mon espace restaurateur
                  </Typography>
                  <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.78rem' }}>
                    Gérez vos commandes, votre menu et vos stats
                  </Typography>
                </Box>
              </Box>
            </Box>
            <Button component={Link} to="/partner/restaurant"
              startIcon={<DashboardIcon sx={{ fontSize: '1rem !important' }} />}
              sx={{
                py: 1.2, px: 2.5, borderRadius: '12px', textTransform: 'none',
                fontWeight: 800, fontSize: '0.82rem', letterSpacing: 0, flexShrink: 0,
                width: { xs: '100%', sm: 'auto' },
                bgcolor: C.gold, color: C.purpleDark,
                boxShadow: `0 4px 16px rgba(253,185,39,0.4)`,
                position: 'relative', zIndex: 1,
                '&:hover': { boxShadow: `0 6px 24px rgba(253,185,39,0.55)`, transform: 'translateY(-1px)' },
              }}>
              Accéder au tableau de bord
            </Button>
          </Box>
        )}

        {/* ── TABLEAU DE BORD LIVREUR ── */}
        {user?.user_type === 'delivery' && (
          <Box sx={{
            p: 3, mb: 3, borderRadius: '20px',
            background: `linear-gradient(135deg, #0a1628 0%, #1a365d 55%, #2563eb 100%)`,
            boxShadow: `0 8px 32px rgba(37,99,235,0.35)`,
            display: 'flex', alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: 'space-between',
            flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 2, sm: 0 },
            position: 'relative', overflow: 'hidden',
            transition: 'all 0.22s',
            '&:hover': { transform: 'translateY(-3px)', boxShadow: `0 16px 48px rgba(37,99,235,0.45)` },
            '&::before': {
              content: '""', position: 'absolute',
              top: '-40%', right: '-10%',
              width: '60%', height: '200%',
              background: `radial-gradient(ellipse, rgba(253,185,39,0.18) 0%, transparent 65%)`,
            },
          }}>
            <Box sx={{ position: 'relative', zIndex: 1 }}>
              <Box display="flex" alignItems="center" gap={1.5} sx={{ mb: 0.6 }}>
                <Box sx={{
                  width: 40, height: 40, borderRadius: '12px',
                  bgcolor: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255,255,255,0.25)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                  <LocalShippingIcon sx={{ color: C.gold, fontSize: 20 }} />
                </Box>
                <Box>
                  <Typography sx={{ color: 'white', fontWeight: 900, fontSize: '1.05rem', lineHeight: 1.2 }}>
                    Mon espace livreur
                  </Typography>
                  <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.78rem' }}>
                    Gérez vos livraisons et configurez vos paiements
                  </Typography>
                </Box>
              </Box>
            </Box>
            <Box display="flex" gap={1.5} flexDirection={{ xs: 'column', sm: 'row' }} sx={{ position: 'relative', zIndex: 1 }}>
              <Button component={Link} to="/partner/delivery"
                startIcon={<DashboardIcon sx={{ fontSize: '1rem !important' }} />}
                sx={{
                  py: 1.2, px: 2.5, borderRadius: '12px', textTransform: 'none',
                  fontWeight: 800, fontSize: '0.82rem', letterSpacing: 0, flexShrink: 0,
                  bgcolor: C.gold, color: C.purpleDark,
                  boxShadow: `0 4px 16px rgba(253,185,39,0.4)`,
                  '&:hover': { boxShadow: `0 6px 24px rgba(253,185,39,0.55)`, transform: 'translateY(-1px)' },
                }}>
                Tableau de bord
              </Button>
              <Button component={Link} to="/partner/stripe/onboard"
                startIcon={<SettingsIcon sx={{ fontSize: '1rem !important' }} />}
                sx={{
                  py: 1.2, px: 2.5, borderRadius: '12px', textTransform: 'none',
                  fontWeight: 800, fontSize: '0.82rem', letterSpacing: 0, flexShrink: 0,
                  bgcolor: 'rgba(255,255,255,0.15)', color: 'white',
                  border: '1px solid rgba(255,255,255,0.3)',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.25)', transform: 'translateY(-1px)' },
                }}>
                Configurer Stripe
              </Button>
            </Box>
          </Box>
        )}

        {/* ── DEVENIR PARTENAIRE ── */}
        <Box sx={{
          p: 3, mb: 4, borderRadius: '20px',
          background: `linear-gradient(135deg, ${C.purpleDark} 0%, ${C.purple} 60%, ${C.purpleLight} 100%)`,
          boxShadow: `0 8px 32px rgba(85,37,131,0.35)`,
          display: 'flex', alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: 'space-between',
          flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 2, sm: 0 },
          textAlign: { xs: 'center', sm: 'left' },
          position: 'relative', overflow: 'hidden',
          transition: 'all 0.22s',
          '&:hover': { transform: 'translateY(-3px)', boxShadow: `0 16px 48px rgba(85,37,131,0.45)` },
          '&::before': {
            content: '""', position: 'absolute',
            top: '-40%', right: '-10%',
            width: '60%', height: '200%',
            background: `radial-gradient(ellipse, rgba(253,185,39,0.2) 0%, transparent 65%)`,
          },
        }}>
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Box display="flex" gap={0.8} sx={{ mb: 1.2 }}>
              {[DirectionsCarIcon, PedalBikeIcon, RestaurantMenuIcon].map((Icon, idx) => (
                <Icon key={idx} sx={{ fontSize: '1.3rem', color: C.gold }} />
              ))}
            </Box>
            <Typography sx={{ color: 'white', fontWeight: 900, fontSize: '1.05rem', mb: 0.4 }}>
              Devenez partenaire
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.8rem' }}>
              Chauffeur, livreur ou restaurateur
            </Typography>
          </Box>
          <Button component={Link} to="/partner/register"
            endIcon={<ArrowForwardIcon sx={{ fontSize: '0.85rem !important' }} />}
            sx={{
              py: 1.2, px: 2.5, borderRadius: '12px', textTransform: 'none',
              fontWeight: 800, fontSize: '0.82rem', letterSpacing: 0, flexShrink: 0,
              width: { xs: '100%', sm: 'auto' },
              bgcolor: C.gold, color: C.purpleDark,
              boxShadow: `0 4px 16px rgba(253,185,39,0.4)`,
              position: 'relative', zIndex: 1,
              '&:hover': { bgcolor: C.goldLight, boxShadow: `0 6px 24px rgba(253,185,39,0.55)`, transform: 'translateY(-1px)' },
            }}>
            Rejoindre
          </Button>
        </Box>

        {/* ── SERVICES ── */}
        <Typography sx={{ color: C.text, fontSize: '1.3rem', fontWeight: 900, letterSpacing: '-0.01em', mb: 2 }}>
          Nos services
        </Typography>

        <Grid container spacing={2} sx={{ mb: 5 }}>
          {SERVICES.map(({ title, desc, icon: Icon, path, historyPath, image, color, bg, border, comingSoon }) => (
            <Grid item xs={12} sm={6} md={4} key={title}>
              <Box sx={{
                borderRadius: '20px', overflow: 'hidden',
                boxShadow: '0 4px 20px rgba(85,37,131,0.08)',
                border: `1px solid rgba(85,37,131,0.08)`,
                transition: 'all 0.25s cubic-bezier(0.4,0,0.2,1)',
                opacity: comingSoon ? 0.82 : 1,
                ...(!comingSoon && {
                  '&:hover': {
                    transform: 'translateY(-6px)',
                    boxShadow: `0 20px 50px rgba(85,37,131,0.16)`,
                    border: `1px solid ${border}`,
                    '& .service-img': { transform: 'scale(1.07)' },
                    '& .service-overlay': { opacity: 0.55 },
                  },
                }),
              }}>
                {/* Image avec overlay */}
                <Box sx={{ position: 'relative', height: 160, overflow: 'hidden' }}>
                  <Box className="service-img" sx={{
                    position: 'absolute', inset: 0,
                    backgroundImage: `url(${image})`,
                    backgroundSize: 'cover', backgroundPosition: 'center',
                    transition: 'transform 0.45s cubic-bezier(0.4,0,0.2,1)',
                    filter: comingSoon ? 'grayscale(40%)' : 'none',
                  }} />
                  <Box className="service-overlay" sx={{
                    position: 'absolute', inset: 0,
                    background: comingSoon
                      ? 'linear-gradient(135deg, rgba(80,80,80,0.7) 0%, rgba(30,30,30,0.75) 100%)'
                      : `linear-gradient(135deg, ${color}cc 0%, ${C.purpleDark}99 100%)`,
                    opacity: comingSoon ? 1 : 0.45,
                    transition: 'opacity 0.3s',
                  }} />

                  {/* Badge Bientôt disponible */}
                  {comingSoon && (
                    <Box sx={{
                      position: 'absolute', top: 14, right: 14,
                      bgcolor: 'rgba(253,185,39,0.95)',
                      color: C.purpleDark,
                      px: 1.5, py: 0.5, borderRadius: '8px',
                      fontSize: '0.65rem', fontWeight: 900,
                      letterSpacing: '0.06em', textTransform: 'uppercase',
                      boxShadow: '0 2px 10px rgba(253,185,39,0.4)',
                    }}>
                      Bientôt disponible
                    </Box>
                  )}

                  {/* Icône + titre */}
                  <Box sx={{ position: 'absolute', inset: 0, p: 2.5, display: 'flex', alignItems: 'flex-end' }}>
                    <Box sx={{
                      width: 42, height: 42, borderRadius: '12px',
                      bgcolor: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(255,255,255,0.35)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      mr: 1.5, flexShrink: 0,
                    }}>
                      <Icon sx={{ color: 'white', fontSize: 22 }} />
                    </Box>
                    <Typography sx={{ color: 'white', fontWeight: 900, fontSize: '1.05rem', letterSpacing: '-0.01em', textShadow: '0 1px 8px rgba(0,0,0,0.4)' }}>
                      {title}
                    </Typography>
                  </Box>
                </Box>

                {/* Bas de la card */}
                <Box sx={{ p: 2.5, bgcolor: 'rgba(255,255,255,0.85)', backdropFilter: 'blur(20px)' }}>
                  <Typography sx={{ color: C.textSoft, fontSize: '0.8rem', mb: 2, lineHeight: 1.5 }}>
                    {desc}
                  </Typography>
                  {comingSoon ? (
                    <Box sx={{
                      py: 1, px: 2, borderRadius: '10px', textAlign: 'center',
                      bgcolor: 'rgba(85,37,131,0.05)', border: `1px solid rgba(85,37,131,0.1)`,
                    }}>
                      <Typography sx={{ color: C.textMuted, fontSize: '0.78rem', fontWeight: 600 }}>
                        <RocketLaunchIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'text-bottom' }} /> Disponible prochainement
                      </Typography>
                    </Box>
                  ) : (
                    <Box display="flex" gap={1}>
                      <Button component={Link} to={path} size="small" sx={{
                        flex: 1, py: 0.9, borderRadius: '10px', textTransform: 'none',
                        fontWeight: 700, fontSize: '0.78rem', letterSpacing: 0,
                        bgcolor: bg, color, border: `1px solid ${border}`,
                        '&:hover': { bgcolor: `${color}20`, boxShadow: `0 4px 12px ${color}28` },
                      }}>
                        Accéder
                      </Button>
                      <Button component={Link} to={historyPath} size="small" sx={{
                        py: 0.9, px: 1.8, borderRadius: '10px', textTransform: 'none',
                        fontWeight: 600, fontSize: '0.75rem', letterSpacing: 0,
                        color: C.textMuted, border: `1px solid rgba(85,37,131,0.1)`,
                        '&:hover': { color: C.purple, border: `1px solid rgba(85,37,131,0.25)`, bgcolor: `rgba(85,37,131,0.04)` },
                      }}>
                        Historique
                      </Button>
                    </Box>
                  )}
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>

        {/* ── BANDE INFOS ── */}
        <Grid container spacing={2}>

          {/* Profil */}
          <Grid item xs={12} md={6}>
            <Box sx={{
              p: 3, borderRadius: '20px',
              bgcolor: 'rgba(255,255,255,0.75)',
              backdropFilter: 'blur(20px)',
              border: `1px solid rgba(85,37,131,0.08)`,
              boxShadow: '0 4px 20px rgba(85,37,131,0.06)',
              display: 'flex', alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: 'space-between',
              flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 2, sm: 0 },
              transition: 'all 0.22s',
              '&:hover': { boxShadow: '0 8px 32px rgba(85,37,131,0.1)', border: `1px solid rgba(85,37,131,0.18)` },
            }}>
              <Box display="flex" alignItems="center" gap={2}>
                <Avatar sx={{
                  width: 50, height: 50,
                  background: `linear-gradient(135deg, ${C.purple}, ${C.purpleLight})`,
                  fontWeight: 800, fontSize: '1rem',
                  boxShadow: `0 4px 16px rgba(85,37,131,0.3)`,
                }}>
                  {initials || <PersonIcon />}
                </Avatar>
                <Box>
                  <Typography sx={{ color: C.text, fontWeight: 700, fontSize: '0.95rem', mb: 0.2 }}>
                    {user?.first_name} {user?.last_name}
                  </Typography>
                  <Typography sx={{ color: C.textMuted, fontSize: '0.75rem' }}>
                    {user?.email}
                  </Typography>
                  {user?.phone && (
                    <Typography sx={{ color: C.textMuted, fontSize: '0.72rem' }}>
                      {user.phone}
                    </Typography>
                  )}
                </Box>
              </Box>
              <Button component={Link} to="/profile" endIcon={<ArrowForwardIcon sx={{ fontSize: '0.85rem !important' }} />}
                sx={{
                  py: 1, px: 2, borderRadius: '11px', textTransform: 'none',
                  fontWeight: 700, fontSize: '0.78rem', letterSpacing: 0, flexShrink: 0,
                  color: C.purple, border: `1px solid rgba(85,37,131,0.2)`,
                  '&:hover': { bgcolor: `rgba(85,37,131,0.07)`, borderColor: `rgba(85,37,131,0.35)` },
                }}>
                Gérer
              </Button>
            </Box>
          </Grid>

        </Grid>

      </Box>
    </Box>
  );
};

export default Dashboard;
